import { useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { View, Text, Pressable, ScrollView, Linking } from "react-native";
import ReAnimated, {
  FadeIn,
  FadeOut,
  LinearTransition,
} from "react-native-reanimated";
import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import { Image } from "expo-image";
import {
  Calendar,
  Check,
  ClipboardList,
  ExternalLink,
  FileText,
  Folder,
  Pencil,
  Plus,
  Pill,
  CalendarCheck,
  CalendarClock,
  RefreshCw,
  Trash2,
  NotebookPen,
} from "lucide-react-native";
import {
  useDeleteDocument,
  useGetDownloadUrl,
  useUpdateDocument,
} from "@src/packages/documents/hooks/useDocuments";
import type {
  Document,
  Upload,
} from "@src/packages/documents/hooks/useDocuments";
import {
  useCreateTestResults,
  useTestResults,
} from "@src/packages/test-results/hooks/useTestResults";
import type { DataPointSampleType } from "@src/packages/content/types";
import { useApiClient } from "@src/packages/api/louise-api";
import type { DocumentProcessingRunLookup } from "@src/packages/documents/ingestion/documentProcessingRun";
import {
  findDocumentValues,
  isSemenAnalysisRun,
  type DocumentValueDraft,
} from "@src/packages/documents/ingestion/documentValues";
import { findDocumentProcessingLookup } from "@src/packages/documents/ingestion/documentValueReview";
import {
  DocumentValuesReview,
  type DocumentSavedValue,
} from "@src/packages/document-vault/DocumentValuesReview";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { useAppointments } from "@src/packages/care/hooks/useAppointments";
import {
  AddMedicationModal,
  type EditMedicationData,
  type PrefillMedicationData,
} from "./care/modals/AddMedicationModal";
import {
  AddAppointmentModal,
  type AppointmentPrefill,
} from "./care/modals/AddAppointmentModal";
import type { AppointmentScheduleItem } from "./care/schedule.types";
import { useTranslations } from "@src/packages/i18n";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import { EMBER } from "../../onboarding/theme";
import {
  CompactActionButton,
  DateField,
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  IconButton,
  MoonDetailRow,
  MoonFilterChip,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  SupportingSubtitleText,
  TextActionButton,
  TextField,
  DialogBodyText,
  MoonSkeleton,
  useToast,
} from "@louise/moon-ui";
import { DocumentRow } from "./documents/DocumentRow";
import {
  CATEGORIES,
  getDocumentCategoryFromTags,
  type DocumentCategory,
} from "./DocumentsScreen";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const DELETE_CONFIRMATION_ILLUSTRATION =
  require("../../assets/images/you-icons/trash-bin.png") as number;

// ── Types ──

export type ClinicalValue = {
  label: string;
  value: string;
  unit?: string;
  referenceRange?: string;
  flag?: "normal" | "low" | "high" | "critical";
};

export type PreviewDocumentInfo = {
  slotId: string;
  slotName: string;
  slotWhy: string;
  document: Document;
  clinicalValues?: ClinicalValue[];
  /**
   * How the document was read, when the caller already has it: the
   * Documents preview fixture passes one so its values can be seen without
   * an upload job behind them. Otherwise the modal looks it up.
   */
  processingLookup?: DocumentProcessingRunLookup | null;
} | null;

type Props = {
  visible: boolean;
  onClose: () => void;
  documentInfo: PreviewDocumentInfo;
  onReupload: () => void;
};

function isImageContentType(contentType: string | null): boolean {
  if (!contentType) return false;
  return contentType.startsWith("image/");
}

/**
 * A date the app stored reads in the patient's format, on its own calendar
 * day. Text read off the document itself ("May 16") stays as written.
 */
function displayDate(value: string, formatDate: (isoDate: string) => string) {
  const isoDay = /^\d{4}-\d{2}-\d{2}/.exec(value)?.[0];
  return isoDay ? formatDate(isoDay) : value;
}

/** The catalogue a document's "Add a value" offers, by what it reports. */
function sampleTypesForDocument(
  doctype: Doctype,
  semenAnalysis: boolean,
): DataPointSampleType[] | null {
  if (semenAnalysis) return ["semen"];
  if (doctype === "lab") return ["blood", "semen", "urine", "saliva"];
  if (doctype === "imaging") return ["ultrasound", "imaging"];
  if (doctype === "genetic") return ["genetic", "blood", "saliva"];
  return null;
}

// ── Tag parsing helpers ──
//
// Documents carry tags like:
//   doctype:treatment_plan
//   med:NAME|DOSAGE UNIT|FREQUENCY|START_DATE|TIME_OF_DAY
//   appt:TITLE|TYPE|DATE|TIME
//
// We use them to decide which sections to render (per doctype) and to show
// read-only summaries of the meds / appointments linked to the doc.

export type Doctype =
  | "lab"
  | "treatment_plan"
  | "appointment_letter"
  | "imaging"
  | "genetic"
  | "visit_report"
  | "other";

function parseDoctype(tags: string[]): Doctype {
  const tag = tags.find((t) => t.startsWith("doctype:"));
  if (!tag) return "other";
  const value = tag.slice("doctype:".length);
  switch (value) {
    case "lab":
    case "treatment_plan":
    case "appointment_letter":
    case "imaging":
    case "genetic":
    case "visit_report":
      return value;
    default:
      return "other";
  }
}

export type LinkedMedSummary = {
  name: string;
  dosage: string;
  frequency: string;
  startDate: string;
  timeOfDay: string;
};

export type LinkedAppointmentSummary = {
  title: string;
  type: string;
  date: string;
  time: string;
};

function parseLinkedMeds(tags: string[]): LinkedMedSummary[] {
  return tags
    .filter((t) => t.startsWith("med:"))
    .map((t) => {
      const [name, dosage, frequency, startDate, timeOfDay] = t
        .slice("med:".length)
        .split("|");
      return {
        name: name ?? "",
        dosage: dosage ?? "",
        frequency: frequency ?? "",
        startDate: startDate ?? "",
        timeOfDay: timeOfDay ?? "",
      };
    })
    .filter((m) => m.name.trim().length > 0);
}

function parseLinkedAppointments(tags: string[]): LinkedAppointmentSummary[] {
  return tags
    .filter((t) => t.startsWith("appt:"))
    .map((t) => {
      const [title, type, date, time] = t.slice("appt:".length).split("|");
      return {
        title: title ?? "",
        type: type ?? "",
        date: date ?? "",
        time: time ?? "",
      };
    })
    .filter((a) => a.title.trim().length > 0);
}

/**
 * Turn a med tag extracted off a document into the shape AddMedicationModal
 * seeds its form with. We split "2.5 mg" into dose + unit; whatever we can't
 * parse (route, delivery, times) the patient picks in the modal.
 */
function tagToPrefillMedication(tag: LinkedMedSummary): PrefillMedicationData {
  const dosageMatch = /^\s*([\d.]+)\s*(.+?)\s*$/.exec(tag.dosage);
  const dose = dosageMatch ? Number(dosageMatch[1]) : null;
  const doseUnit = dosageMatch ? dosageMatch[2] : tag.dosage || null;
  const notes =
    [tag.frequency, tag.timeOfDay].filter((v) => v && v.trim()).join(" · ") ||
    null;
  return {
    name: tag.name,
    dose: Number.isFinite(dose) ? dose : null,
    doseUnit,
    startDate: tag.startDate || undefined,
    notes,
  };
}

/**
 * Turn an appt tag extracted off a document into the shape AddAppointmentModal
 * seeds its form with. Type/activities are left for the patient to pick.
 */
function tagToPrefillAppointment(
  tag: LinkedAppointmentSummary,
): AppointmentPrefill {
  return {
    title: tag.title || undefined,
    date: tag.date || undefined,
    startTime: tag.time || undefined,
  };
}

// ── Modal Content ──

function PreviewContent({
  onClose,
  documentInfo,
  onReupload,
}: {
  onClose: () => void;
  documentInfo: PreviewDocumentInfo;
  onReupload: () => void;
}) {
  const capturePress = usePressCapture("PreviewContent");
  const t = useTranslations();
  const reducedMotion = useReducedMotion();
  const { showToast } = useToast();
  const { abbrev, format: formatDate } = useDateFormat();
  // Preview sections unfold top-to-bottom when the modal opens. 70ms between
  // sections reads as intentional without making the tail feel laggy; capped
  // so a modal with many sections never leaves the last one visibly late.
  const sectionEnter = (idx: number) =>
    reducedMotion
      ? undefined
      : FadeIn.delay(Math.min(idx * 70, 350)).duration(260);
  const { client, patientId } = useApiClient();
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  // CTA modals — opened from the read-only linked sections.
  const [showAddMedication, setShowAddMedication] = useState(false);
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  // When the patient taps an extracted row that has no matching live record
  // yet, we open the real Add modal pre-filled with the tag's values so a
  // tap on "Letrozole 2.5 mg AM" becomes a real medication on the calendar.
  const [prefillingMedication, setPrefillingMedication] =
    useState<PrefillMedicationData | null>(null);
  const [prefillingAppointment, setPrefillingAppointment] =
    useState<AppointmentPrefill | null>(null);
  // When the user taps a linked record we open the corresponding modal in
  // edit mode with the real DB record pre-filled.
  const [editingMedication, setEditingMedication] =
    useState<EditMedicationData | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<
    (AppointmentScheduleItem & { date: string }) | null
  >(null);

  const [pendingCategory, setPendingCategory] =
    useState<DocumentCategory | null>(null);
  // Per-row metadata edit, matching the AppointmentDetailSheet pattern: pen
  // in the row's trailing slot flips just that row to an inline editor so a
  // scroll past the metadata never risks a stray edit, and the patient can
  // correct one field (usually the OCR-read date) without touching the other.
  const [editingField, setEditingField] = useState<"name" | "date" | null>(
    null,
  );
  const [nameDraft, setNameDraft] = useState("");
  const [dateDraft, setDateDraft] = useState("");
  const [dateDraftValid, setDateDraftValid] = useState(true);
  const deleteDocument = useDeleteDocument();
  const updateDocument = useUpdateDocument();
  const getDownloadUrl = useGetDownloadUrl();
  const { data: allTestResults } = useTestResults();
  const createTestResults = useCreateTestResults();

  const document = documentInfo?.document ?? null;
  const uploadedUploads = useMemo(
    () => document?.uploads.filter((u) => u.uploadStatus === "uploaded") ?? [],
    [document],
  );
  const isReadOnly = document?.readOnly === true;
  const fixtureLookup = documentInfo?.processingLookup;
  const uploadIds = useMemo(
    () => document?.uploads.map((upload) => upload.id) ?? [],
    [document],
  );

  // How the document was read: its run, and the evidence entity values
  // added to it link to. A document saved before documents were read, or
  // one carried over from Olly, has none.
  const lookupQuery = useQuery({
    queryKey: ["document-processing-lookup", patientId, document?.id],
    queryFn: () =>
      findDocumentProcessingLookup(client, patientId ?? "", uploadIds),
    enabled:
      fixtureLookup === undefined &&
      !!patientId &&
      !!document &&
      document.origin !== "legacy_olly" &&
      uploadIds.length > 0,
    staleTime: 30 * 1000,
  });
  const lookup =
    fixtureLookup !== undefined ? fixtureLookup : (lookupQuery.data ?? null);
  const run = lookup?.state === "ready" ? lookup.run : null;
  const sourceEntityId = lookup?.sourceEntityId ?? run?.sourceEntityId ?? null;

  // Results saved against this document's file before it was read, through
  // the earlier review forms. They stay visible, read-only.
  const earlierValues = useMemo<DocumentSavedValue[]>(() => {
    if (!allTestResults || !document) return [];
    const ids = new Set(document.uploads.map((u) => u.id));
    return allTestResults
      .filter(
        (tr) => tr.attachment?.uploadId && ids.has(tr.attachment.uploadId),
      )
      .map((tr) => ({
        id: tr.id,
        name: tr.name ?? "",
        value: tr.patientValue ?? tr.value ?? tr.doctorValue ?? "",
        unit: tr.unit ?? "",
        date: tr.date ?? null,
      }));
  }, [allTestResults, document]);
  const resultNotes = useMemo(() => {
    if (!allTestResults || !document) return "";
    const ids = new Set(document.uploads.map((u) => u.id));
    return allTestResults
      .filter(
        (tr) =>
          tr.notes &&
          tr.attachment?.uploadId &&
          ids.has(tr.attachment.uploadId),
      )
      .map((tr) => tr.notes)
      .join("\n\n");
  }, [allTestResults, document]);

  // Parse doctype + linked med/appt summaries from the document's tags.
  // The inline editable forms were replaced with CTAs that open the real
  // AddMedicationModal / AddAppointmentModal. Linked rows themselves are
  // pressable: tapping one opens the same modal in edit mode pre-filled
  // with the live DB record (so the displayed name/dose/date is always
  // whatever the user last saved, not a stale tag snapshot).
  const doctype = useMemo<Doctype>(
    () => parseDoctype(documentInfo?.document.tags ?? []),
    [documentInfo],
  );
  const linkedMedTags = useMemo(
    () => parseLinkedMeds(documentInfo?.document.tags ?? []),
    [documentInfo],
  );
  const linkedAppointmentTags = useMemo(
    () => parseLinkedAppointments(documentInfo?.document.tags ?? []),
    [documentInfo],
  );

  // Fetch live records to enrich the linked summaries.
  const { data: allPatientMeds = [] } = usePatientMedications();
  const appointmentsRange = useMemo(() => {
    // Wide window so we can find any appointment the doc references, past
    // or future, regardless of when the doc was uploaded.
    const now = new Date();
    const start = new Date(now.getFullYear() - 1, 0, 1);
    const end = new Date(now.getFullYear() + 1, 11, 31);
    const toIso = (d: Date) => d.toISOString().slice(0, 10);
    return { startDate: toIso(start), endDate: toIso(end) };
  }, []);
  const { data: allAppointments = [] } = useAppointments(appointmentsRange);

  // For each tag, try to find the matching live record. Match by
  // case-insensitive name (med) or title (appt). When found we render the
  // live data; when not found we fall back to the tag snapshot so the row
  // still shows something.
  type LinkedMedRow = {
    tag: ReturnType<typeof parseLinkedMeds>[number];
    edit: EditMedicationData | null;
  };
  type LinkedAppointmentRow = {
    tag: ReturnType<typeof parseLinkedAppointments>[number];
    edit: (AppointmentScheduleItem & { date: string }) | null;
  };

  const linkedMeds = useMemo<LinkedMedRow[]>(() => {
    return linkedMedTags.map((tag) => {
      const match = allPatientMeds.find((m) => {
        const dbName = (m.medication?.name ?? m.rawMedicationName ?? "")
          .toString()
          .toLowerCase()
          .trim();
        return dbName.length > 0 && dbName === tag.name.toLowerCase().trim();
      });
      if (!match) return { tag, edit: null };
      const edit: EditMedicationData = {
        id: match.id,
        name: match.medication?.name ?? match.rawMedicationName ?? tag.name,
        dose: match.dose ?? null,
        doseUnit: match.doseUnit ?? null,
        route: match.route ?? null,
        form: match.form ?? null,
        timeTaken: Array.isArray(match.timeTaken)
          ? (match.timeTaken as string[])
          : typeof match.timeTaken === "string"
            ? [match.timeTaken]
            : undefined,
        startDate: match.startDate ?? undefined,
        endDate: match.endDate ?? null,
        notes: match.notes ?? null,
      };
      return { tag, edit };
    });
  }, [linkedMedTags, allPatientMeds]);

  const linkedAppointments = useMemo<LinkedAppointmentRow[]>(() => {
    return linkedAppointmentTags.map((tag) => {
      const match = allAppointments.find((a) => {
        const apiTitle = (a.title ?? "").toString().toLowerCase().trim();
        return (
          apiTitle.length > 0 && apiTitle === tag.title.toLowerCase().trim()
        );
      });
      if (!match) return { tag, edit: null };
      // Server returns startDate as ISO datetime — keep only the YYYY-MM-DD
      // part for the date field; pull HH:MM out of startTime / startDate.
      const date = match.startDate?.slice(0, 10) ?? tag.date ?? "";
      const timeIso = /T(\d{2}):(\d{2})/.exec(match.startDate ?? "");
      const time =
        match.startTime?.slice(0, 5) ??
        (timeIso ? `${timeIso[1]}:${timeIso[2]}` : tag.time);
      const edit: AppointmentScheduleItem & { date: string } = {
        type: "appointment",
        id: match.id,
        appointmentId: match.id,
        title: match.title ?? tag.title,
        subtitle: match.meetingRoom ?? "",
        time,
        date,
        icon: CalendarClock,
        consultationType: match.consultationType ?? null,
        location: match.meetingRoom ?? null,
        isOnline: match.isOnline ?? null,
        meetingLink: match.meetingLink ?? null,
        notes: match.description ?? null,
      };
      return { tag, edit };
    });
  }, [linkedAppointmentTags, allAppointments]);

  const doDelete = useCallback(async () => {
    if (isReadOnly) return;
    if (!documentInfo) return;
    setDeleting(true);
    try {
      await deleteDocument.mutateAsync(documentInfo.document.id);
      setDeleteConfirmVisible(false);
      onClose();
    } catch (error) {
      console.error("Delete failed:", error);
      setDeleting(false);
    }
  }, [documentInfo, deleteDocument, isReadOnly, onClose]);

  // The shared delete confirmation on every platform: the web build has no
  // native alert, and window.confirm looked nothing like the app.
  const handleDelete = useCallback(() => {
    if (isReadOnly) return;
    if (!documentInfo) return;
    setDeleteConfirmVisible(true);
  }, [documentInfo, isReadOnly]);

  // Opening an edit row seeds its draft from the live document so a prior
  // save (OCR correction, another device) is reflected, not overwritten.
  const beginEditName = useCallback(() => {
    if (isReadOnly || !document) return;
    setNameDraft(document.name);
    setEditingField("name");
  }, [document, isReadOnly]);
  const beginEditDate = useCallback(() => {
    if (isReadOnly || !document) return;
    setDateDraft(document.documentDate ?? "");
    setDateDraftValid(true);
    setEditingField("date");
  }, [document, isReadOnly]);
  const cancelMetaEdit = useCallback(() => setEditingField(null), []);
  const saveName = useCallback(async () => {
    if (isReadOnly || !document) return;
    const trimmed = nameDraft.trim();
    if (trimmed.length === 0 || trimmed === document.name) {
      setEditingField(null);
      return;
    }
    try {
      await updateDocument.mutateAsync({
        documentId: document.id,
        data: { name: trimmed },
      });
      setEditingField(null);
    } catch (error) {
      console.error("Update document name failed:", error);
    }
  }, [document, isReadOnly, nameDraft, updateDocument]);
  const saveDate = useCallback(async () => {
    if (isReadOnly || !document) return;
    const next = dateDraft || null;
    if (next === (document.documentDate || null)) {
      setEditingField(null);
      return;
    }
    try {
      await updateDocument.mutateAsync({
        documentId: document.id,
        data: { documentDate: next },
      });
      setEditingField(null);
    } catch (error) {
      console.error("Update document date failed:", error);
    }
  }, [document, isReadOnly, dateDraft, updateDocument]);

  // A document no upload job produced has no evidence entity to link an
  // observation to, so its added values keep the result-with-attachment
  // write it has always had.
  const saveUnlinkedValues = useCallback(
    async (values: DocumentValueDraft[]) => {
      if (!documentInfo || values.length === 0) return;
      const uploadId = uploadedUploads[0]?.id ?? null;
      await createTestResults.mutateAsync(
        values.map((value) => ({
          name: value.name,
          patientValue: value.value || null,
          unit: value.unit || null,
          type: "blood" as const,
          bloodTestCategory: "hormonal" as const,
          valueType: "numeric" as const,
          date: value.date ?? documentInfo.document.documentDate ?? null,
          attachmentId: uploadId,
        })),
      );
    },
    [createTestResults, documentInfo, uploadedUploads],
  );

  const primaryUpload: Upload | null = uploadedUploads[0] ?? null;
  const isImage = isImageContentType(primaryUpload?.contentType ?? null);

  const { data: downloadUrl = null, isLoading: loadingUrl } = useQuery({
    queryKey: ["download-url", primaryUpload?.id],
    queryFn: async () => {
      if (!primaryUpload?.id) return null;
      const url = await getDownloadUrl.mutateAsync({
        uploadId: primaryUpload.id,
        disposition: "attachment",
      });
      return typeof url === "string" ? url : null;
    },
    enabled: !!primaryUpload?.id,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const handleOpenFile = useCallback(async () => {
    if (!primaryUpload?.id) return;
    try {
      const url =
        downloadUrl ??
        (await getDownloadUrl.mutateAsync({
          uploadId: primaryUpload.id,
          disposition: "attachment",
        }));
      if (typeof url === "string" && url.length > 0) {
        await Linking.openURL(url);
      }
    } catch (error) {
      console.error("Open document failed:", error);
    }
  }, [downloadUrl, getDownloadUrl, primaryUpload?.id]);

  if (!documentInfo || !document) return null;

  const documentDate = document.documentDate;
  const foundValueCount = run
    ? findDocumentValues(run, documentDate).length
    : 0;
  const readsAsResults =
    run?.classificationCode === "lab_result" ||
    run?.classificationCode === "imaging_result";
  const showValues =
    foundValueCount > 0 ||
    earlierValues.length > 0 ||
    (!isReadOnly &&
      (doctype === "lab" ||
        doctype === "imaging" ||
        doctype === "genetic" ||
        readsAsResults));
  const uploadedAt = primaryUpload?.createdAt ?? null;

  return (
    <>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ maxHeight: 640 }}
      >
        <DialogHeader
          title={documentInfo.slotName}
          subtitle={documentInfo.slotWhy}
          style={{ marginBottom: 18 }}
        />

        {/* The file itself, inline. No sub-dialog, no filename repeat: the
            DialogHeader above already names it. */}
        {primaryUpload ? (
          <ReAnimated.View
            style={{ marginBottom: 14 }}
            entering={sectionEnter(0)}
          >
            {isImage ? (
              downloadUrl ? (
                <Image
                  source={{ uri: downloadUrl }}
                  style={{
                    width: "100%",
                    height: 280,
                    borderRadius: 12,
                    backgroundColor: "rgba(0,0,0,0.04)",
                  }}
                  contentFit="contain"
                  cachePolicy="memory-disk"
                />
              ) : (
                <MoonSkeleton width="100%" height={280} radius={12} />
              )
            ) : (
              <PrimaryButton
                label={t("documents.preview.open_document")}
                action="documents.preview.open_document"
                onPress={handleOpenFile}
                loading={loadingUrl && !downloadUrl}
                leading={
                  <ExternalLink size={16} color="#fff" strokeWidth={2} />
                }
                fullWidth
              />
            )}
          </ReAnimated.View>
        ) : (
          <DocumentRow
            key={document.id}
            title={document.name}
            dateLabel={
              documentDate ? abbrev(documentDate) : t("documents.no_date")
            }
            icon={
              <FileText size={17} color={EMBER.colors.warm} strokeWidth={1.6} />
            }
            status="missing"
            action="open_document_file"
            onPress={() => undefined}
            elevated={false}
            testID="document-preview-file-row"
          />
        )}

        {/* File metadata — labeled rows for scannability. Each editable row
          carries a pen in its trailing slot that flips the value to an inline
          editor (matches the AppointmentDetailSheet pattern). Patients come
          here mostly to correct the OCR-read date. "Uploaded" stays read-only
          — it is a system fact. */}
        {primaryUpload ? (
          <ReAnimated.View
            style={{ gap: 12, marginBottom: 16 }}
            entering={sectionEnter(1)}
          >
            <MoonDetailRow
              icon={Calendar}
              label={t("documents.preview.info.record_date")}
              value={
                editingField === "date" ? (
                  <DocumentMetaDateEditor
                    draft={dateDraft}
                    onChange={setDateDraft}
                    onValidityChange={setDateDraftValid}
                    isValid={dateDraftValid}
                    isSaving={updateDocument.isPending}
                    onCancel={cancelMetaEdit}
                    onSave={() => void saveDate()}
                  />
                ) : documentDate ? (
                  formatDate(documentDate)
                ) : (
                  t("documents.no_date")
                )
              }
              trailing={
                !isReadOnly && editingField !== "date" ? (
                  <EditPen
                    label={t("documents.preview.edit_record_date")}
                    action="documents.preview.edit_record_date"
                    onPress={beginEditDate}
                    testID="document-edit-date"
                  />
                ) : undefined
              }
            />
            <MoonDetailRow
              icon={ClipboardList}
              label={t("documents.preview.info.document_name")}
              value={
                editingField === "name" ? (
                  <DocumentMetaNameEditor
                    draft={nameDraft}
                    onChange={setNameDraft}
                    isSaving={updateDocument.isPending}
                    onCancel={cancelMetaEdit}
                    onSave={() => void saveName()}
                  />
                ) : (
                  document.name
                )
              }
              trailing={
                !isReadOnly && editingField !== "name" ? (
                  <EditPen
                    label={t("documents.preview.edit_document_name")}
                    action="documents.preview.edit_document_name"
                    onPress={beginEditName}
                    testID="document-edit-name"
                  />
                ) : undefined
              }
            />
            {uploadedAt ? (
              <MoonDetailRow
                icon={FileText}
                label={t("documents.preview.info.uploaded")}
                value={formatDate(uploadedAt)}
              />
            ) : null}
          </ReAnimated.View>
        ) : null}

        {/* Category assignment — only when the doc has no category set. */}
        {!isReadOnly &&
        getDocumentCategoryFromTags(document.tags) === "uncategorized" ? (
          <ReAnimated.View
            style={{ marginBottom: 18 }}
            entering={sectionEnter(2)}
            exiting={reducedMotion ? undefined : FadeOut.duration(220)}
            layout={reducedMotion ? undefined : LinearTransition.springify()}
          >
            <View style={{ marginBottom: 10 }}>
              <SectionTitle icon={Folder}>
                {t("documents.preview.category.assign_prompt")}
              </SectionTitle>
            </View>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              {CATEGORIES.map((category) => (
                <MoonFilterChip
                  key={category.key}
                  label={t(category.labelKey)}
                  action={`documents.preview.category.${category.key}`}
                  icon={category.icon}
                  selected={false}
                  showCheck={false}
                  disabled={pendingCategory !== null}
                  onPress={() => {
                    setPendingCategory(category.key);
                    void updateDocument
                      .mutateAsync({
                        documentId: document.id,
                        data: { tags: [...document.tags, category.key] },
                      })
                      .catch((error: unknown) => {
                        // Surface the failure so the user is not left wondering
                        // why their pick did nothing, and log the identifiers
                        // that locate the document on the server so a 404 or
                        // ownership mismatch is diagnosable.
                        const serverMessage =
                          error instanceof Error
                            ? error.message
                            : String(error);
                        console.error("Assign category failed:", {
                          serverMessage,
                          documentId: document.id,
                          origin: document.origin,
                          readOnly: document.readOnly,
                          tags: document.tags,
                          // Compare these two: if they differ, the server is
                          // refusing because this patient does not own the doc
                          // (visible via a shared view but not owned).
                          sessionPatientId: patientId,
                          documentPatientId: document.patientId,
                        });
                        showToast({
                          variant: "error",
                          message: t(
                            "documents.preview.category.assign_failed",
                            { params: { reason: serverMessage } },
                          ),
                        });
                      })
                      .finally(() => {
                        setPendingCategory(null);
                      });
                  }}
                />
              ))}
            </View>
          </ReAnimated.View>
        ) : null}

        {showValues ? (
          <ReAnimated.View style={{ marginTop: 22 }} entering={sectionEnter(3)}>
            <DocumentValuesReview
              run={run}
              sourceEntityId={sourceEntityId}
              loading={fixtureLookup === undefined && lookupQuery.isLoading}
              stillReading={
                lookup?.state === "not_ready" ||
                run?.status === "queued" ||
                run?.status === "running"
              }
              documentName={document.name}
              documentDate={documentDate}
              earlierValues={earlierValues}
              sampleTypes={sampleTypesForDocument(
                doctype,
                run ? isSemenAnalysisRun(run) : false,
              )}
              isReadOnly={isReadOnly}
              onSaveUnlinkedValues={saveUnlinkedValues}
              onNotNow={onClose}
            />
          </ReAnimated.View>
        ) : null}

        {/* Each section brings its own space above, so none of them leaves a
            gap under the values when they are all empty. */}
        <View>
          {/* Linked medications: a read-only summary parsed from doc tags. The
            old inline editable form here was incomplete (missing fields vs the
            real AddMedicationModal) and the records it created never landed on
            the schedule reliably. The CTA below opens the real AddMedicationModal. */}
          {(linkedMeds.length > 0 ||
            doctype === "treatment_plan" ||
            doctype === "visit_report") && (
            <ReAnimated.View
              style={{ marginTop: 22 }}
              entering={sectionEnter(4)}
            >
              <View style={{ marginBottom: 10 }}>
                <SectionTitle icon={Pill}>
                  {t("documents.preview.section.medications")}
                </SectionTitle>
              </View>
              {linkedMeds.length === 0 ? (
                <SupportingSubtitleText
                  style={{ marginTop: 0, marginBottom: 12 }}
                >
                  {t("documents.preview.medications_empty")}
                </SupportingSubtitleText>
              ) : (
                linkedMeds.map(({ tag, edit }, idx) => {
                  // Prefer live DB data when we found a matching record so the
                  // displayed fields reflect the latest edits, not the original
                  // tag snapshot.
                  const displayName = edit?.name ?? tag.name;
                  const displayDose =
                    edit?.dose != null
                      ? `${edit.dose}${edit.doseUnit ? ` ${edit.doseUnit}` : ""}`
                      : tag.dosage;
                  const displayFrequency = edit?.notes ?? tag.frequency;
                  const displayTime =
                    (edit?.timeTaken && edit.timeTaken.length > 0
                      ? edit.timeTaken.join(", ")
                      : null) ?? tag.timeOfDay;
                  const displayStart = edit?.startDate ?? tag.startDate;
                  const subtitle = [displayDose, displayFrequency, displayTime]
                    .filter(Boolean)
                    .join(" · ");
                  const entering = reducedMotion
                    ? undefined
                    : FadeIn.delay(Math.min(idx * 40, 400)).duration(220);
                  return (
                    <ReAnimated.View
                      key={`med-${idx}`}
                      entering={entering}
                      layout={
                        reducedMotion ? undefined : LinearTransition.springify()
                      }
                    >
                      <Pressable
                        onPress={() => {
                          if (isReadOnly) return;
                          capturePress(
                            pressLabel(displayName),
                            edit
                              ? "edit_medication_row"
                              : "prefill_medication_row",
                          );
                          if (edit) {
                            setEditingMedication(edit);
                          } else {
                            setPrefillingMedication(
                              tagToPrefillMedication(tag),
                            );
                          }
                        }}
                        disabled={isReadOnly}
                        style={({ pressed }) => ({
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 12,
                          paddingVertical: 12,
                          paddingHorizontal: 12,
                          marginTop: idx === 0 ? 0 : 8,
                          borderRadius: 10,
                          borderWidth: 1,
                          borderColor: edit
                            ? "rgba(0,0,0,0.06)"
                            : EMBER.colors.warm,
                          borderStyle: edit ? "solid" : "dashed",
                          backgroundColor: edit
                            ? "transparent"
                            : "rgba(255, 237, 213, 0.4)",
                          opacity: pressed ? 0.6 : 1,
                        })}
                      >
                        <View style={{ flex: 1 }}>
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 14,
                              fontWeight: "600",
                              color: EMBER.colors.text,
                            }}
                          >
                            {displayName}
                          </Text>
                          {subtitle && (
                            <Text
                              className="font-dm-sans"
                              style={{
                                fontSize: 12,
                                color: EMBER.colors.textMid,
                                marginTop: 2,
                              }}
                            >
                              {subtitle}
                            </Text>
                          )}
                          {displayStart && (
                            <Text
                              className="font-dm-sans"
                              style={{
                                fontSize: 11,
                                color: EMBER.colors.textLight,
                                marginTop: 2,
                              }}
                            >
                              {t("documents.preview.medications_start", {
                                params: {
                                  date: displayDate(displayStart, abbrev),
                                },
                              })}
                            </Text>
                          )}
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 11,
                              fontWeight: "600",
                              color: edit
                                ? EMBER.colors.textMid
                                : EMBER.colors.warm,
                              marginTop: 4,
                            }}
                          >
                            {edit
                              ? t("documents.preview.added_to_calendar")
                              : t("documents.preview.add_to_calendar_hint")}
                          </Text>
                        </View>
                        {edit ? (
                          <Check
                            size={18}
                            color={EMBER.colors.textMid}
                            strokeWidth={2}
                          />
                        ) : (
                          <Plus
                            size={18}
                            color={EMBER.colors.warm}
                            strokeWidth={2}
                          />
                        )}
                      </Pressable>
                    </ReAnimated.View>
                  );
                })
              )}
              {!isReadOnly ? (
                <CompactActionButton
                  label={t(
                    linkedMeds.length > 0
                      ? "documents.preview.add_another_medication"
                      : "documents.preview.add_medication",
                  )}
                  action="documents.preview.add_medication"
                  variant="secondary"
                  leadingIcon={Plus}
                  onPress={() => setShowAddMedication(true)}
                  style={{ marginTop: 12 }}
                />
              ) : null}
            </ReAnimated.View>
          )}

          {/* Linked appointments: a read-only summary parsed from doc tags. CTA
            opens the real AddAppointmentModal which validates date + time and
            creates a real appointment on the calendar. */}
          {(linkedAppointments.length > 0 ||
            doctype === "appointment_letter" ||
            doctype === "visit_report") && (
            <ReAnimated.View
              style={{ marginTop: 22 }}
              entering={sectionEnter(5)}
            >
              <View style={{ marginBottom: 10 }}>
                <SectionTitle icon={CalendarCheck}>
                  {t("documents.preview.section.appointments")}
                </SectionTitle>
              </View>
              {linkedAppointments.length === 0 ? (
                <SupportingSubtitleText
                  style={{ marginTop: 0, marginBottom: 12 }}
                >
                  {t("documents.preview.appointments_empty")}
                </SupportingSubtitleText>
              ) : (
                linkedAppointments.map(({ tag, edit }, idx) => {
                  const displayTitle = edit?.title ?? tag.title;
                  const displayType =
                    (edit?.consultationType && edit.consultationType[0]) ??
                    tag.type;
                  const appointmentDate = edit?.date ?? tag.date;
                  const displayTime = edit?.time ?? tag.time;
                  const subtitle = [
                    displayType,
                    appointmentDate && displayDate(appointmentDate, abbrev),
                    displayTime,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  const entering = reducedMotion
                    ? undefined
                    : FadeIn.delay(Math.min(idx * 40, 400)).duration(220);
                  return (
                    <ReAnimated.View
                      key={`appt-${idx}`}
                      entering={entering}
                      layout={
                        reducedMotion ? undefined : LinearTransition.springify()
                      }
                    >
                      <Pressable
                        onPress={() => {
                          if (isReadOnly) return;
                          capturePress(
                            pressLabel(displayTitle),
                            edit
                              ? "edit_appointment_row"
                              : "prefill_appointment_row",
                          );
                          if (edit) {
                            setEditingAppointment(edit);
                          } else {
                            setPrefillingAppointment(
                              tagToPrefillAppointment(tag),
                            );
                          }
                        }}
                        disabled={isReadOnly}
                        style={({ pressed }) => ({
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 12,
                          paddingVertical: 12,
                          paddingHorizontal: 12,
                          marginTop: idx === 0 ? 0 : 8,
                          borderRadius: 10,
                          borderWidth: 1,
                          borderColor: edit
                            ? "rgba(0,0,0,0.06)"
                            : EMBER.colors.warm,
                          borderStyle: edit ? "solid" : "dashed",
                          backgroundColor: edit
                            ? "transparent"
                            : "rgba(255, 237, 213, 0.4)",
                          opacity: pressed ? 0.6 : 1,
                        })}
                      >
                        <View style={{ flex: 1 }}>
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 14,
                              fontWeight: "600",
                              color: EMBER.colors.text,
                            }}
                          >
                            {displayTitle}
                          </Text>
                          {subtitle && (
                            <Text
                              className="font-dm-sans"
                              style={{
                                fontSize: 12,
                                color: EMBER.colors.textMid,
                                marginTop: 2,
                              }}
                            >
                              {subtitle}
                            </Text>
                          )}
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 11,
                              fontWeight: "600",
                              color: edit
                                ? EMBER.colors.textMid
                                : EMBER.colors.warm,
                              marginTop: 4,
                            }}
                          >
                            {edit
                              ? t("documents.preview.added_to_calendar")
                              : t("documents.preview.add_to_calendar_hint")}
                          </Text>
                        </View>
                        {edit ? (
                          <Check
                            size={18}
                            color={EMBER.colors.textMid}
                            strokeWidth={2}
                          />
                        ) : (
                          <Plus
                            size={18}
                            color={EMBER.colors.warm}
                            strokeWidth={2}
                          />
                        )}
                      </Pressable>
                    </ReAnimated.View>
                  );
                })
              )}
              {!isReadOnly ? (
                <CompactActionButton
                  label={t(
                    linkedAppointments.length > 0
                      ? "documents.preview.add_another_appointment"
                      : "documents.preview.add_appointment",
                  )}
                  action="documents.preview.add_appointment"
                  variant="secondary"
                  leadingIcon={Plus}
                  onPress={() => setShowAddAppointment(true)}
                  style={{ marginTop: 12 }}
                />
              ) : null}
            </ReAnimated.View>
          )}

          {/* Notes / findings */}
          {resultNotes ? (
            <ReAnimated.View
              style={{ marginTop: 22 }}
              entering={sectionEnter(6)}
            >
              <MoonDetailRow
                icon={NotebookPen}
                label={t("documents.preview.section.notes_findings")}
                value={
                  <DialogBodyText style={{ marginTop: 0 }}>
                    {resultNotes}
                  </DialogBodyText>
                }
              />
            </ReAnimated.View>
          ) : null}

          {/* Replace / Delete — directly in the modal, no sub-dialog. */}
          {!isReadOnly && primaryUpload ? (
            <ReAnimated.View
              style={{
                flexDirection: "row",
                justifyContent: "center",
                gap: 20,
                marginTop: 24,
              }}
              entering={sectionEnter(7)}
            >
              <TextActionButton
                label={t("documents.preview.replace_file")}
                action="documents.preview.replace_file"
                icon={RefreshCw}
                onPress={onReupload}
              />
              <TextActionButton
                label={t("documents.preview.delete_document")}
                action="documents.preview.delete_document"
                tone="destructive"
                icon={Trash2}
                onPress={handleDelete}
                disabled={deleting}
                testID="document-delete"
              />
            </ReAnimated.View>
          ) : null}
        </View>
      </ScrollView>

      <Dialog
        visible={deleteConfirmVisible}
        onClose={() => {
          if (!deleting) setDeleteConfirmVisible(false);
        }}
        closeButton="minimal"
        dismissOnBackdropTap={!deleting}
        testID="document-delete-confirmation-dialog"
      >
        <DialogIcon source={DELETE_CONFIRMATION_ILLUSTRATION} />
        <DialogHeader
          align="center"
          title={t("documents.preview.delete_this_document")}
          subtitle={t("documents.preview.delete_confirm_message", {
            params: { name: documentInfo.slotName },
          })}
          style={{ marginTop: 16, marginBottom: 20 }}
        />
        <DialogActions style={{ marginTop: 0 }}>
          <SecondaryButton
            label={t("documents.preview.cancel")}
            action="documents.preview.cancel"
            onPress={() => setDeleteConfirmVisible(false)}
            disabled={deleting}
            flex={1}
          />
          <SecondaryButton
            variant="destructive"
            label={t("documents.preview.delete")}
            action="documents.preview.delete"
            onPress={() => void doDelete()}
            loading={deleting}
            flex={1}
          />
        </DialogActions>
      </Dialog>
      <AddMedicationModal
        visible={
          !isReadOnly &&
          (showAddMedication ||
            editingMedication != null ||
            prefillingMedication != null)
        }
        editMedication={editingMedication}
        prefillMedication={prefillingMedication}
        onClose={() => {
          setShowAddMedication(false);
          setEditingMedication(null);
          setPrefillingMedication(null);
        }}
      />
      <AddAppointmentModal
        visible={
          !isReadOnly &&
          (showAddAppointment ||
            editingAppointment != null ||
            prefillingAppointment != null)
        }
        editAppointment={editingAppointment}
        prefill={prefillingAppointment}
        onClose={() => {
          setShowAddAppointment(false);
          setEditingAppointment(null);
          setPrefillingAppointment(null);
        }}
      />
    </>
  );
}

// ── Inline metadata editors ──

function EditPen({
  label,
  action,
  onPress,
  testID,
}: {
  label: string;
  action: string;
  onPress: () => void;
  testID?: string;
}) {
  return (
    <IconButton
      accessibilityLabel={label}
      action={action}
      onPress={onPress}
      size="sm"
      surface="soft"
      testID={testID}
    >
      <Pencil size={14} color={EMBER.colors.textLight} strokeWidth={1.8} />
    </IconButton>
  );
}

function DocumentMetaNameEditor({
  draft,
  onChange,
  isSaving,
  onCancel,
  onSave,
}: {
  draft: string;
  onChange: (value: string) => void;
  isSaving: boolean;
  onCancel: () => void;
  onSave: () => void;
}) {
  const t = useTranslations();
  return (
    <View style={{ gap: 9, minWidth: 0 }}>
      <TextField
        testID="document-name-input"
        accessibilityLabel={t("documents.preview.info.document_name")}
        editable={!isSaving}
        value={draft}
        onChangeText={onChange}
      />
      <View
        style={{
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
        }}
      >
        <TextActionButton
          label={t("documents.preview.cancel")}
          action="documents.preview.cancel_edit_name"
          onPress={onCancel}
          disabled={isSaving}
          size="sm"
        />
        <CompactActionButton
          label={t("documents.preview.save_changes")}
          action="documents.preview.save_document_name"
          onPress={onSave}
          loading={isSaving}
          minWidth={88}
        />
      </View>
    </View>
  );
}

function DocumentMetaDateEditor({
  draft,
  onChange,
  onValidityChange,
  isValid,
  isSaving,
  onCancel,
  onSave,
}: {
  draft: string;
  onChange: (value: string) => void;
  onValidityChange: (valid: boolean) => void;
  isValid: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSave: () => void;
}) {
  const t = useTranslations();
  return (
    <View style={{ gap: 9, minWidth: 0 }}>
      <DateField
        testID="document-date-input"
        accessibilityLabel={t("documents.preview.info.record_date")}
        value={draft}
        onChange={onChange}
        onValidityChange={onValidityChange}
      />
      <View
        style={{
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
        }}
      >
        <TextActionButton
          label={t("documents.preview.cancel")}
          action="documents.preview.cancel_edit_date"
          onPress={onCancel}
          disabled={isSaving}
          size="sm"
        />
        <CompactActionButton
          label={t("documents.preview.save_changes")}
          action="documents.preview.save_record_date"
          onPress={onSave}
          loading={isSaving}
          disabled={!isValid}
          minWidth={88}
        />
      </View>
    </View>
  );
}

// ── Exported Modal ──

export default function DocumentPreviewModal({
  visible,
  onClose,
  documentInfo,
  onReupload,
}: Props) {
  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout="centered"
      closeButton="minimal"
      contentStyle={{
        paddingTop: 24,
        paddingHorizontal: 24,
        paddingBottom: 24,
      }}
    >
      <PreviewContent
        onClose={onClose}
        documentInfo={documentInfo}
        onReupload={onReupload}
      />
    </Dialog>
  );
}
